import { normalizeError } from '../api/core/errors.ts';

/**
 * The bank's own wording for the failures the generic mapper cannot name.
 *
 * Kept pure and apart from the store so the suite can reach it: the store itself is all requests.
 */

/** No IZTeach teacher session at all - the bank cannot be reached until /setup is done. */
export const SETUP_SIGNED_OUT = 'Kho bài tập dùng chung với IZTeach. Thầy/cô đăng nhập tài khoản giáo viên IZTeach ở trang Cài đặt (/setup) để dùng kho.';

/** A session exists but it is the student token from the QLT exchange, which the bank refuses. */
export const SETUP_NOT_TEACHER = 'Phiên IZTeach hiện tại không phải tài khoản giáo viên nên không mở được kho bài tập. Thầy/cô đăng nhập tài khoản giáo viên IZTeach ở trang Cài đặt (/setup).';

/** The gateway authorizer refused the token's role; the empty 403 body says nothing useful. */
export const FORBIDDEN = 'Tài khoản IZTeach này không có quyền với kho bài tập. Thầy/cô đăng nhập tài khoản giáo viên ở trang Cài đặt (/setup).';

/** The IZ account could not be read, so "Của tôi" cannot filter. */
export const MINE_UNKNOWN = 'Không đọc được tài khoản IZTeach nên chưa lọc được "Của tôi". Thầy/cô đăng nhập lại ở trang Cài đặt (/setup).';

/** How the teacher gets an IZ teacher session back: /setup, or with SSO a fresh QLT login (there is no /setup). */
export type SessionRemedy = 'setup' | 'sso';

export interface SessionMessages {
  signedOut: string;
  notTeacher: string;
  forbidden: string;
  mineUnknown: string;
}

const SSO_RELOGIN = 'Thầy/cô đăng xuất rồi đăng nhập lại bảng để kết nối IZTeach.';

const SESSION_MESSAGES: Record<SessionRemedy, SessionMessages> = {
  setup: { signedOut: SETUP_SIGNED_OUT, notTeacher: SETUP_NOT_TEACHER, forbidden: FORBIDDEN, mineUnknown: MINE_UNKNOWN },
  sso: {
    signedOut: `Kho bài tập dùng chung với IZTeach. Bảng chưa kết nối được tài khoản giáo viên IZTeach. ${SSO_RELOGIN}`,
    notTeacher: `Phiên IZTeach hiện tại không phải tài khoản giáo viên nên không mở được kho bài tập. ${SSO_RELOGIN}`,
    forbidden: `Tài khoản IZTeach này không có quyền với kho bài tập. ${SSO_RELOGIN} Nếu vẫn lỗi, thầy/cô báo quản trị viên.`,
    mineUnknown: `Không đọc được tài khoản IZTeach nên chưa lọc được "Của tôi". ${SSO_RELOGIN}`,
  },
};

export function sessionMessages(remedy: SessionRemedy = 'setup'): SessionMessages {
  return SESSION_MESSAGES[remedy];
}

/** A 403 here is the role check, not a missing question. Null for every other failure. */
export function forbiddenMessage(error: unknown, remedy: SessionRemedy = 'setup'): string | null {
  return normalizeError(error).kind === 'forbidden' ? sessionMessages(remedy).forbidden : null;
}

/**
 * Sequential saves stop at the first failure, so the teacher is told exactly where: what landed,
 * which one failed, and how many were never sent.
 */
export function partialSaveMessage(saved: number, total: number, reason: string): string {
  const failedAt = saved + 1;
  const unsent = total - failedAt;
  const head = saved > 0 ? `Đã lưu ${saved}/${total} câu hỏi. ` : '';
  const tail = unsent > 0 ? ` ${unsent} câu sau đó chưa được gửi.` : '';
  return `${head}Câu ${failedAt} không lưu được: ${reason}${tail}`;
}

/** The note when a page holds question types the board can only name; the count and pager include them. */
export function hiddenTypesMessage(hidden: number): string {
  return `Trang này có ${hidden} câu hỏi dạng khác (ma trận, kéo thả...) chỉ xem và sửa được trên trang quản trị IZTeach.`;
}
